/** @format */
import { validStatus, handleText } from "utils/fetch";
import { getProp, setProp } from "utils/utils";
import tgamConfig from "config/tgam";

const API_ENDPOINT = "https://usasync01.admantx.com/admantx/service";
const ENV_TIMEOUT = parseInt(getProp(tgamConfig, "env.adIasAdmantxApiTimeout", 1000), 10);
const STATE_NAMESPACE = "adops.admantx";

/**
 * Get a `STATE_NAMESPACE` property
 * @param {String} prop - the key name
 * @param {Object} config
 * @returns {String|null} the value
 */
export function get(prop, config = tgamConfig) {
  const val = getProp(config, `${STATE_NAMESPACE}.${prop}`, null);
  return val;
}

/**
 * Get a list of properties
 * @param {Array} props
 * @returns {Array}
 */
export function getProps(props = []) {
  return props.map(p => get(p));
}

/**
 * Sets a `STATE_NAMESPACE` property
 * @param {String} prop - the key name
 * @param {Object} val - the value
 * @param {Object} config
 */
function set(prop, val, config = tgamConfig) {
  const key = `${STATE_NAMESPACE}.${prop}`;
  setProp(config, key, val);
}

/**
 * Process an Admantx API response and determines if
 * the article is brand unsafe.
 * @param {Object} res - the Admantx API response
 * @returns {Promise<String[]>}
 */
function processResponse(res) {
  const { admants = [], categories = [], descriptor = "", valid = "" } = res;
  const invalidResponse = valid !== "OK"; // documentation page 30
  const documentNotYetProcessed = descriptor === "other"; // documentation page 6
  const processedAdmants = admants.map(a => a.name);
  const processedCategories = categories.map(sanitizeCategory);
  if (invalidResponse || documentNotYetProcessed) {
    processedAdmants.push("brand_unsafe");
  }
  set("response", res);
  set("admants", processedAdmants);
  set("categories", processedCategories);
  return Promise.resolve({
    admants: processedAdmants,
    categories: processedCategories
  });
}

/**
 * Replaces `/` with `-` and removes all non alpha-numeric
 * characters except dashes and spaces.
 * @param {String} category.name - the category name
 * @returns {String}
 */
function sanitizeCategory({ name }) {
  return name
    .replace(/\//g, "-")
    .replace(/[^A-Za-z0-9\-\s]/g, "")
    .trim();
}

/**
 * Parses text into JSON
 * @param {String} text - API response text
 * @returns {Promise<Object>}
 */
function parseText(text) {
  return Promise.resolve(JSON.parse(text));
}

/**
 * Creates a stringified object of Admantx API parameters
 * @param {String} uri - the article uri
 * @returns {String} - the API request string
 */
function query(uri) {
  const params = {
    key: "fba7213d19e7bcc2d9f919ee442dc5c25cc5a643aacaf5283a2fdaf314837244",
    type: "URL",
    method: "descriptor",
    mode: "async",
    filter: ["admants", "categories"],
    body: `https://www.theglobeandmail.com${encodeURIComponent(uri)}` // documentation page 6
  };
  const q = JSON.stringify(params);
  return q;
}

/**
 * Get the Admantx service URI
 * @param {Object} config
 * @returns {String|null} - the Admantx API endpoint or `null` when applicable
 */
function getRequestUri(config = tgamConfig) {
  const uri = getProp(config, "meta.urlRelative");
  const story = getProp(config, "meta.pagetype") === "story";
  const subType = getProp(config, "meta.pagesubtype");
  const inValidStoryType = story && ["video", "gallery", "interactive"].includes(subType);
  const isAdmin = getProp(config, "env.isPbAdmin", false);
  const isComposerPreview = getProp(config, "env.isEllipsisPreview", false);
  if (!story || inValidStoryType || isAdmin || isComposerPreview) {
    return null;
  }
  const requestUri = `${API_ENDPOINT}?request=${query(uri)}`;
  set("requestUri", requestUri);
  return requestUri;
}

/**
 * Fetches the Admantx API and process the response data
 * @returns {Promise}
 */
export default function initAdmantx() {
  const service = getRequestUri();
  if (!service) {
    return Promise.resolve();
  }
  const controller = new AbortController(); // https://javascript.info/fetch-abort
  const { signal } = controller;
  set("timeout", ENV_TIMEOUT);
  setTimeout(() => controller.abort(), ENV_TIMEOUT);
  return fetch(service, { signal })
    .then(validStatus)
    .then(handleText)
    .then(parseText) // API responds with `text/plain` Content-Type
    .then(processResponse)
    .catch(err => {
      set("admants", ["brand_unsafe"]); // in the event of any error/timeout etc.
      console.error(
        err.name === "AbortError"
          ? `Request cancelled, ${ENV_TIMEOUT}ms budget exceeded.`
          : err.message
      );
    });
}
